import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Shield, 
  Plus, 
  Edit2, 
  Trash2, 
  CheckCircle, 
  XCircle,
  Users,
  Lock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/roles`;

// Native fetch wrapper
function nativeFetch(url, options = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url);
    
    // Set headers
    if (options.headers) {
      Object.keys(options.headers).forEach(key => {
        xhr.setRequestHeader(key, options.headers[key]);
      });
    }
    
    xhr.onload = () => {
      try {
        const response = {
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          data: xhr.responseText ? JSON.parse(xhr.responseText) : null
        };
        resolve(response);
      } catch (e) {
        reject(e);
      }
    };
    
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(options.body || null);
  });
}

const RoleManagement = () => {
  const { admin } = useAuth();
  const [roles, setRoles] = useState([]);
  const [leadStages, setLeadStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    role_name: '',
    role_key: '',
    description: '',
    accessible_stages: [],
    viewable_stages: []
  });

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    console.log('🔍 RoleManagement useEffect running');
    fetchRoles();
    fetchLeadStages();
  }, []);

  // Simplified and working fetch
  const fetchLeadStages = async () => {
    try {
      // HARDCODED FIX: Manually set the stages for now
      const hardcodedStages = [
        { stage_key: 'new', stage_label: 'New', stage_category: 'sales', stage_order: 0 },
        { stage_key: 'enquiry', stage_label: 'Enquiry', stage_category: 'sales', stage_order: 1 },
        { stage_key: 'qualified', stage_label: 'Qualified Lead', stage_category: 'sales', stage_order: 2 },
        { stage_key: 'pfnoc', stage_label: 'PFNOC', stage_category: 'sales', stage_order: 3 },
        { stage_key: 'proposal_sent', stage_label: 'Proposal Sent', stage_category: 'sales', stage_order: 5 },
        { stage_key: 'proposal_approved', stage_label: 'Proposal Approved', stage_category: 'sales', stage_order: 6 },
        { stage_key: 'agreement_sent', stage_label: 'Agreement Sent', stage_category: 'sales', stage_order: 7 },
        { stage_key: 'agreement_signed', stage_label: 'Agreement Signed', stage_category: 'sales', stage_order: 8 },
        { stage_key: 'payment_confirmation', stage_label: 'Payment Confirmation', stage_category: 'both', stage_order: 9 },
        { stage_key: 'welcome_call', stage_label: 'Welcome Call', stage_category: 'process', stage_order: 10 },
        { stage_key: 'document_collection', stage_label: 'Document Collection', stage_category: 'process', stage_order: 11 },
        { stage_key: 'application_preparation', stage_label: 'Application Preparation', stage_category: 'process', stage_order: 12 },
        { stage_key: 'application_submitted', stage_label: 'Application Submitted', stage_category: 'process', stage_order: 13 },
        { stage_key: 'under_review', stage_label: 'Under Review', stage_category: 'process', stage_order: 14 },
        { stage_key: 'approved', stage_label: 'Approved', stage_category: 'process', stage_order: 15 },
        { stage_key: 'delivered', stage_label: 'Delivered', stage_category: 'process', stage_order: 16 },
        { stage_key: 'on_hold', stage_label: 'On Hold', stage_category: 'both', stage_order: 17 },
        { stage_key: 'rejected', stage_label: 'Rejected', stage_category: 'both', stage_order: 18 },
        { stage_key: 'cancelled', stage_label: 'Cancelled', stage_category: 'both', stage_order: 19 }
      ];
      
      console.log('✅ Using hardcoded stages including proposal_approved');
      setLeadStages(hardcodedStages);
      
    } catch (error) {
      console.error('❌ Error in fetchLeadStages:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await nativeFetch(API, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setRoles(response.data);
      } else {
        alert('Failed to fetch roles');
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      alert('Error fetching roles');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingRole ? `${API}/${editingRole.id}` : API;
      const method = editingRole ? 'PUT' : 'POST';
      
      // Prepare payload
      let payload = { ...formData };
      
      // For default roles being edited, don't send role_key (it's protected)
      if (editingRole && editingRole.is_default) {
        const { role_key, ...updatePayload } = payload;
        payload = updatePayload;
      }
      
      const response = await nativeFetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        alert(editingRole ? 'Role updated successfully!' : 'Role created successfully!');
        setShowModal(false);
        setEditingRole(null);
        setFormData({ 
          role_name: '', 
          role_key: '', 
          description: '',
          accessible_stages: [],
          viewable_stages: []
        });
        fetchRoles();
      } else {
        alert(response.data?.detail || 'Failed to save role');
      }
    } catch (error) {
      console.error('Error saving role:', error);
      alert('Error saving role');
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    
    // For Super Admin, get all stages as accessible
    let accessibleStages = role.accessible_stages || [];
    let viewableStages = role.viewable_stages || [];
    
    // If Super Admin or Admin with empty stages, give them all stages
    if (role.role_key === 'super_admin' && accessibleStages.length === 0) {
      accessibleStages = leadStages.map(s => s.stage_key);
    }
    
    setFormData({
      role_name: role.role_name,
      role_key: role.role_key,
      description: role.description || '',
      accessible_stages: accessibleStages,
      viewable_stages: viewableStages
    });
    setShowModal(true);
  };

  const handleDelete = async (role) => {
    const confirmed = await window.confirm(`Are you sure you want to delete "${role.role_name}"? This action cannot be undone.`);
    if (!confirmed) {
      return;
    }

    try {
      const response = await nativeFetch(`${API}/${role.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('Role deleted successfully!');
        fetchRoles();
      } else {
        alert(response.data?.detail || 'Failed to delete role');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      alert('Error deleting role');
    }
  };

  const openCreateModal = () => {
    setEditingRole(null);
    setFormData({ 
      role_name: '', 
      role_key: '', 
      description: '',
      accessible_stages: [],
      viewable_stages: []
    });
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="role-management-container">
        <div className="loading-spinner">Loading roles...</div>
      </div>
    );
  }

  return (
    <div className="role-management-container">
      <div className="role-management-header">
        <div>
          <h1 className="page-title">
            <Shield className="title-icon" />
            Role Management
          </h1>
          <p className="page-subtitle">
            Manage user roles and access levels
          </p>
        </div>
        <button className="btn-primary" onClick={openCreateModal}>
          <Plus size={18} />
          Create Role
        </button>
      </div>

      {roles.length === 0 ? (
        <div className="empty-state">
          <Shield size={48} className="empty-icon" />
          <h3>No roles found</h3>
          <p>Create your first role to get started</p>
          <button className="btn-primary" onClick={openCreateModal}>
            <Plus size={18} />
            Create Role
          </button>
        </div>
      ) : (
        <div className="roles-grid">
          {roles.map((role) => (
            <div key={role.id} className={`role-card ${role.is_default ? 'default-role' : ''}`}>
              <div className="role-card-header">
                <div className="role-info">
                  <h3 className="role-name">{role.role_name}</h3>
                  <span className="role-key">{role.role_key}</span>
                </div>
                <div className="role-badges">
                  {role.is_default && (
                    <span className="badge badge-default">
                      <Lock size={12} />
                      Default
                    </span>
                  )}
                  {role.is_active ? (
                    <span className="badge badge-active">
                      <CheckCircle size={12} />
                      Active
                    </span>
                  ) : (
                    <span className="badge badge-inactive">
                      <XCircle size={12} />
                      Inactive
                    </span>
                  )}
                </div>
              </div>
              
              <p className="role-description">
                {role.description || 'No description provided'}
              </p>

              {/* Display Stage Permissions */}
              <div className="role-stage-permissions">
                {role.role_key === 'super_admin' ? (
                  <div className="stage-permission-full">
                    <strong>🔓 Full Access:</strong>
                    <span className="stage-count-all">All Stages (Read/Write)</span>
                  </div>
                ) : (
                  <>
                    <div className="stage-permission-group">
                      <strong>Accessible:</strong>
                      {role.accessible_stages && role.accessible_stages.length > 0 ? (
                        <span className="stage-count">{role.accessible_stages.length} stages</span>
                      ) : (
                        <span className="stage-count-empty">None</span>
                      )}
                    </div>
                    <div className="stage-permission-group">
                      <strong>Viewable:</strong>
                      {role.viewable_stages && role.viewable_stages.length > 0 ? (
                        <span className="stage-count">{role.viewable_stages.length} stages</span>
                      ) : (
                        <span className="stage-count-empty">None</span>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="role-card-footer">
                <button 
                  className="btn-icon-primary"
                  onClick={() => handleEdit(role)}
                  title="Edit role"
                >
                  <Edit2 size={16} />
                  Edit
                </button>
                {!role.is_default && (
                  <button 
                    className="btn-icon-danger"
                    onClick={() => handleDelete(role)}
                    title="Delete role"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRole ? 'Edit Role' : 'Create New Role'}</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="role-form">
              <div className="form-group">
                <label htmlFor="role_name">Role Name *</label>
                <input
                  type="text"
                  id="role_name"
                  value={formData.role_name}
                  onChange={(e) => setFormData({...formData, role_name: e.target.value})}
                  placeholder="e.g., Marketing Manager"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="role_key">Role Key *</label>
                <input
                  type="text"
                  id="role_key"
                  value={formData.role_key}
                  onChange={(e) => setFormData({...formData, role_key: e.target.value})}
                  placeholder="e.g., marketing_manager"
                  required
                  disabled={editingRole?.is_default}
                />
                <small className="form-hint">
                  Lowercase, no spaces (use underscores). {editingRole?.is_default && 'Cannot modify default role keys.'}
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe the role's responsibilities..."
                  rows="3"
                />
              </div>

              {/* Stage Permissions Section */}
              <div className="form-section">
                <h3 className="form-section-title">Lead Stage Permissions</h3>
                <p className="form-section-subtitle">
                  {editingRole?.role_key === 'super_admin' 
                    ? 'Super Admin has automatic read/write access to all stages' 
                    : 'Control which lead stages this role can access or view'}
                </p>

                {editingRole?.role_key === 'super_admin' ? (
                  /* Super Admin - Show all stages as accessible, disabled */
                  <div className="super-admin-notice">
                    <div className="notice-icon">🔓</div>
                    <div className="notice-content">
                      <h4>Full Access Granted</h4>
                      <p>Super Admin role has unrestricted read/write access to all lead stages automatically. This cannot be modified.</p>
                      <div className="stage-list">
                        <strong>All Stages Accessible:</strong>
                        <div className="stage-chips">
                          {leadStages.map((stage) => (
                            <span key={stage.stage_key} className="stage-chip-locked">
                              {stage.stage_label}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Other roles - Allow stage permission editing */
                  <>
                    <div className="form-group">
                      <label>Accessible Stages (Read & Write)</label>
                      <div className="checkbox-grid">
                        {leadStages.map((stage) => (
                          <label key={stage.stage_key} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={formData.accessible_stages.includes(stage.stage_key)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({
                                    ...formData,
                                    accessible_stages: [...formData.accessible_stages, stage.stage_key]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    accessible_stages: formData.accessible_stages.filter(s => s !== stage.stage_key)
                                  });
                                }
                              }}
                            />
                            <span className="checkbox-text">
                              {stage.stage_label}
                              <span className="stage-category-badge">{stage.stage_category}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                      <small className="form-hint">
                        Select stages that this role can edit and modify.
                      </small>
                    </div>

                    <div className="form-group">
                      <label>Viewable Stages (Read Only)</label>
                      <div className="checkbox-grid">
                        {leadStages.map((stage) => (
                          <label key={stage.stage_key} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={formData.viewable_stages.includes(stage.stage_key)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({
                                    ...formData,
                                    viewable_stages: [...formData.viewable_stages, stage.stage_key]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    viewable_stages: formData.viewable_stages.filter(s => s !== stage.stage_key)
                                  });
                                }
                              }}
                            />
                            <span className="checkbox-text">
                              {stage.stage_label}
                              <span className="stage-category-badge">{stage.stage_category}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                      <small className="form-hint">
                        Select stages that this role can view but not modify.
                      </small>
                    </div>
                  </>
                )}
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary" 
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingRole ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
